import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { format } from 'date-fns';
import ClockIcon from '../../icons/IconLib/ClockIcon';

interface Props {
    value?: string;
    onChange: (time: string, valid: boolean) => void;
    disabled?: boolean;
    placeholder?: string;
}

const TIME_REGEX = /^(0[0-9]|1[0-9]|2[0-3]):[0-5][0-9]$/;

const initialTime = (): string => format(new Date(), 'HH:mm');

const TimeField: React.FC<Props> = ({
    value,
    onChange,
    disabled,
    placeholder = 'hh:mm',
}) => {
    const [time, setTime] = useState<string>(value && value.length > 0 ? value : initialTime());
    const [isValid, setIsValid] = useState<boolean>(true);

    const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const input = e.target.value;
        if (input.length <= 2) {
            setTime(input);
            setIsValid(true);
        } else if (input.length === 3) {
            setTime(`${input.slice(0, 2)}:${input.slice(2)}`);
        } else if (input.length === 5) {
            if (TIME_REGEX.test(input)) {
                setTime(input);
                setIsValid(true);
            } else {
                setIsValid(false);
            }
        }
    };

    useEffect(() => {
        const valid = TIME_REGEX.test(time);
        setIsValid(valid);
        onChange(time, valid);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [time]);

    return (
        <Wrapper $disabled={disabled} $hasError={!isValid}>
            <IconSlot aria-hidden>
                <ClockIcon />
            </IconSlot>
            <Input
                type='text'
                value={time}
                onChange={handleChange}
                placeholder={placeholder}
                disabled={disabled}
                onFocus={(e) => e.currentTarget.select()}
            />
        </Wrapper>
    );
};

export default TimeField;

const Wrapper = styled.div<{ $disabled?: boolean; $hasError: boolean }>`
    width: 100%;
    min-height: 2.875rem;
    box-sizing: border-box;
    display: grid;
    grid-template-columns: auto 1fr;
    align-items: center;
    gap: 0.625rem;
    background: ${({ theme }) => theme.colors.surface};
    border: 1px solid
        ${({ theme, $hasError }) => ($hasError ? theme.colors.danger : theme.colors.inputBorder)};
    border-radius: 0.875rem;
    padding: 0 0.75rem;
    direction: rtl;
    opacity: ${({ $disabled }) => ($disabled ? 0.6 : 1)};
    transition:
        border-color ${({ theme }) => theme.motion.fast} ease,
        box-shadow ${({ theme }) => theme.motion.fast} ease;

    &:hover {
        border-color: ${({ theme, $hasError, $disabled }) =>
            $disabled
                ? undefined
                : $hasError
                  ? theme.colors.danger
                  : theme.colors.inputBorderHover};
    }

    &:focus-within {
        border-color: ${({ theme, $hasError }) =>
            $hasError ? theme.colors.danger : theme.colors.ink};
        box-shadow: ${({ theme }) => theme.shadows.focus};
    }

    @media (max-width: ${({ theme }) => theme.breakpoints.mobile}) {
        min-height: 2.5rem;
        padding: 0 0.625rem;
        gap: 0.5rem;
    }
`;

const IconSlot = styled.span`
    color: ${({ theme }) => theme.colors.icon};
    display: grid;
    place-items: center;
    width: 1.25rem;
    height: 1.25rem;

    svg {
        width: 1.05rem;
        height: 1.05rem;
        flex-shrink: 0;
    }
`;

const Input = styled.input`
    border: 0;
    outline: 0;
    background: transparent;
    -webkit-appearance: none;
    appearance: none;
    font-family: ${({ theme }) => theme.fonts.sans};
    font-size: ${({ theme }) => theme.text.lg};
    line-height: 1.2;
    color: ${({ theme }) => theme.colors.ink};
    width: 100%;
    padding: 0.375rem 0;
    direction: rtl;
    text-align: start;

    &::placeholder {
        color: ${({ theme }) => theme.colors.ink4};
    }

    &:disabled {
        color: ${({ theme }) => theme.colors.ink2};
        cursor: not-allowed;
    }

    @media (max-width: ${({ theme }) => theme.breakpoints.mobile}) {
        font-size: ${({ theme }) => theme.textMobile.lg};
    }
`;
